import { FormLeavingListener } from '@exam-center/components';
import {
  ExamCenterProctorType,
  GetExamCenterProctorByIdDocument,
  useUpdateExamCenterProctorFromExamCenterManagerMutation,
  type UpdateExamCenterProctorFromExamCenterManagerInput,
} from '@exam-center/graphql';
import { useSnackbar } from '@shared/snackbar';
import { useEffect } from 'react';

import { ExamCenterProctorDetailsEditForm } from '../../components';
import {
  EXAM_CENTER_PROCTOR_DETAILS_FORM_FIELDS_NAME,
  useExamCenterProctorDetails,
  useExamCenterProctorDetailsFormContext,
  type ExamCenterProctorDetailsFormInput,
} from '../../providers';

function markUndefinedIfEmptyString(value: string | null | undefined) {
  if (value === '') {
    return undefined;
  }
  return value;
}

export default function ExamCenterProctorDetailsEditFormLogic() {
  const { showPromiseSnackbar } = useSnackbar();

  const { proctor } = useExamCenterProctorDetails();
  const formContext = useExamCenterProctorDetailsFormContext();

  const [updateProctor, { loading: isLoading }] =
    useUpdateExamCenterProctorFromExamCenterManagerMutation({
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GetExamCenterProctorByIdDocument,
          variables: {
            getExamCenterProctorByIdInput: { examCenterProctorId: proctor.id },
          },
        },
      ],
    });

  const handleUpdateProctor = async (data: ExamCenterProctorDetailsFormInput) => {
    const phoneNumber = data.personal.phoneNumber?.replace(/-/g, '');

    const input: UpdateExamCenterProctorFromExamCenterManagerInput = {
      examCenterProctorId: proctor.id,
      info: {
        phoneNumber: markUndefinedIfEmptyString(phoneNumber) ?? null,
        address: markUndefinedIfEmptyString(data.personal.address) ?? null,
        metadata: {
          employeeId: markUndefinedIfEmptyString(data.employeeInfo.code) ?? null,
          position: markUndefinedIfEmptyString(data.employeeInfo.position) ?? null,
          department: markUndefinedIfEmptyString(data.employeeInfo.department) ?? null,
        },
      },
    };

    if (proctor.type === ExamCenterProctorType.Proctor_3) {
      input.info.email = markUndefinedIfEmptyString(data.personal.email) ?? null;
    }

    return await updateProctor({
      variables: {
        input,
      },
    });
  };

  const handleSubmit = async (data: ExamCenterProctorDetailsFormInput) => {
    showPromiseSnackbar({
      message: {
        pending: 'กำลังบันทึกข้อมูล',
        success: 'บันทึกข้อมูลเรียบร้อยแล้ว',
        error: 'บันทึกข้อมูลไม่สำเร็จ',
      },
      promise: () => handleUpdateProctor(data),
    });
  };

  useEffect(() => {
    if (formContext.formState.isSubmitSuccessful) {
      formContext.reset(formContext.getValues());
    }
  }, [formContext]);

  return (
    <>
      <ExamCenterProctorDetailsEditForm
        onSubmit={handleSubmit}
        isSubmitting={isLoading || formContext.formState.isSubmitting}
        data={{
          id: proctor.id,
          type: proctor.type,
          idNumber: proctor.idNumber,
          nameTh: {
            title: proctor.titleTh,
            firstName: [proctor.firstNameTh, proctor.middleNameTh].filter(Boolean).join(' '),
            lastName: proctor.lastNameTh,
          },
          nameEn: {
            title: proctor.titleEn ?? '',
            firstName: [proctor.firstNameEn, proctor.middleNameEn].filter(Boolean).join(' '),
            lastName: proctor.lastNameEn ?? '',
          },
          email: proctor.email ?? '-',
          startedAt: proctor.startedAt,
          endedAt: proctor.endedAt,
          agreementFileUrls: proctor.agreementStorageObjects ?? [],
        }}
        fields={{
          personal: {
            email: {
              name: EXAM_CENTER_PROCTOR_DETAILS_FORM_FIELDS_NAME.email,
            },
            phoneNumber: {
              name: EXAM_CENTER_PROCTOR_DETAILS_FORM_FIELDS_NAME.phoneNumber,
            },
            address: {
              name: EXAM_CENTER_PROCTOR_DETAILS_FORM_FIELDS_NAME.address,
            },
          },
          employeeInfo: {
            employeeId: {
              name: EXAM_CENTER_PROCTOR_DETAILS_FORM_FIELDS_NAME.employeeId,
            },
            position: {
              name: EXAM_CENTER_PROCTOR_DETAILS_FORM_FIELDS_NAME.position,
            },
            department: {
              name: EXAM_CENTER_PROCTOR_DETAILS_FORM_FIELDS_NAME.department,
            },
          },
        }}
      />
      <FormLeavingListener onSubmit={handleSubmit} />
    </>
  );
}
